import React, { createContext, useState, useEffect, useContext } from 'react';

// 创建上下文
const UserContext = createContext();

// 自定义钩子，方便其他组件使用上下文
export const useUser = () => useContext(UserContext);

// 上下文提供者组件
export const UserProvider = ({ children }) => {
  // 用户状态
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 功德点余额
  const [meritPoints, setMeritPoints] = useState(0);
  
  // 模拟从本地存储加载用户数据
  useEffect(() => {
    const loadUserFromLocalStorage = () => {
      try {
        const storedUser = localStorage.getItem('tiandao_user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setMeritPoints(parsedUser.meritPoints || 0);
        }
      } catch (err) {
        console.error('加载用户数据失败:', err);
        setError('加载用户数据失败');
      } finally {
        setLoading(false);
      }
    };
    
    loadUserFromLocalStorage();
  }, []);
  
  // 登录函数
  const login = async (username, password) => {
    try {
      setLoading(true);
      
      // 这里应该是真实的API调用，现在只是模拟
      // 模拟网络延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 模拟用户数据
      const userData = {
        id: 'user123',
        username,
        displayName: '道友_' + username,
        avatar: '/assets/avatar-default.jpg',
        level: 1,
        meritPoints: 100,
        joinDate: new Date().toISOString(),
        skills: [],
        quests: [],
        inventory: []
      };
      
      // 保存到本地存储
      localStorage.setItem('tiandao_user', JSON.stringify(userData));
      
      // 更新状态
      setUser(userData);
      setMeritPoints(userData.meritPoints);
      return { success: true };
    } catch (err) {
      console.error('登录失败:', err);
      setError('登录失败: ' + err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };
  
  // 登出函数
  const logout = () => {
    localStorage.removeItem('tiandao_user');
    setUser(null);
    setMeritPoints(0);
  };
  
  // 注册函数
  const register = async (username, password, email) => {
    try {
      setLoading(true);
      
      // 这里应该是真实的API调用，现在只是模拟
      // 模拟网络延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 模拟用户数据
      const userData = {
        id: 'user' + Date.now(),
        username,
        email,
        displayName: '道友_' + username,
        avatar: '/assets/avatar-default.jpg',
        level: 1,
        meritPoints: 50, // 新用户初始功德点
        joinDate: new Date().toISOString(),
        skills: [],
        quests: [],
        inventory: []
      };
      
      // 保存到本地存储
      localStorage.setItem('tiandao_user', JSON.stringify(userData));
      
      // 更新状态
      setUser(userData);
      setMeritPoints(userData.meritPoints);
      return { success: true };
    } catch (err) {
      console.error('注册失败:', err);
      setError('注册失败: ' + err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };
  
  // 更新功德点
  const updateMeritPoints = (amount) => {
    if (user) {
      const newBalance = meritPoints + amount;
      setMeritPoints(newBalance);
      
      // 更新本地存储
      const updatedUser = { ...user, meritPoints: newBalance };
      localStorage.setItem('tiandao_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return newBalance;
    }
    return meritPoints;
  };
  
  // 更新用户资料
  const updateProfile = (profileData) => {
    if (user) {
      const updatedUser = { ...user, ...profileData };
      localStorage.setItem('tiandao_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return true;
    }
    return false;
  };
  
  // 提供的上下文值
  const value = {
    user,
    loading,
    error,
    meritPoints,
    login,
    logout,
    register,
    updateMeritPoints,
    updateProfile
  };
  
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
